import { motion } from 'framer-motion';
import { Plus, Leaf, Flame } from 'lucide-react';
import type { MenuItem } from '@happyspace/sdk/restaurant';

export default function MenuItemCard({
  item,
  onAdd,
  index = 0,
}: {
  item: MenuItem;
  onAdd: (item: MenuItem) => void;
  index?: number;
}) {
  // Generate Product schema for structured data
  const productSchemaJson = `{
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "${item.title.replace(/"/g, '\\"')}",
    "description": "${(item.description || `${item.title} from Biryani2Go`).replace(/"/g, '\\"')}",
    ${item.image ? `"image": "${item.image.replace(/"/g, '\\"')}"` : `"image": "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/485ee0f5-162d-499f-b44f-69bc80fbabf9/1785626036737-vd8ge.png"`},
    "price": "${item.basePrice?.toFixed(2) || '0.00'}",
    "priceCurrency": "USD",
    "availability": "${item.inStock && item.available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'}",
    "offers": {
      "@type": "Offer",
      "price": "${item.basePrice?.toFixed(2) || '0.00'}",
      "priceCurrency": "USD",
      "availability": "${item.inStock && item.available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'}"
    }
  }`;

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: productSchemaJson }} />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5, delay: Math.min(index * 0.06, 0.4) }}
        className="card-tin overflow-hidden flex flex-col group"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-surface2">
          {item.image ? (
            <img
              src={item.image}
              alt={item.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-ink-2/40">
              <Flame size={32} />
            </div>
          )}
          {item.dietaryFlags?.isVegetarian && (
            <span className="absolute top-3 left-3 w-6 h-6 rounded-md bg-white/95 flex items-center justify-center shadow">
              <Leaf size={14} className="text-emerald-700" />
            </span>
          )}
          {!item.inStock && (
            <div className="absolute inset-0 bg-maroon/60 flex items-center justify-center">
              <span className="text-white text-sm font-semibold uppercase tracking-wide">Sold Out</span>
            </div>
          )}
        </div>
        <div className="p-5 flex flex-col flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-display font-semibold text-lg text-maroon leading-snug">{item.title}</h3>
            <span className="font-semibold text-ember whitespace-nowrap tabular-nums">
              ${item.basePrice?.toFixed(2)}
              {item.unit ? <span className="text-xs text-ink-2 font-normal"> /{item.unit}</span> : null}
            </span>
          </div>
          {item.description && (
            <p className="mt-2 text-sm text-ink-2 leading-relaxed line-clamp-2">{item.description}</p>
          )}
          <button
            onClick={() => onAdd(item)}
            disabled={!item.inStock || !item.available}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border-2 border-ember text-ember font-semibold py-2.5 px-4 text-sm transition-all hover:bg-ember hover:text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-ember"
          >
            <Plus size={16} /> Add to Dabba
          </button>
        </div>
      </motion.div>
    </>
  );
}
